import React, { useState, useMemo } from 'react';
import { Heading } from '../catalyst/heading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../catalyst/table';
import { Text } from '../catalyst/text';
import { Button } from '../catalyst/button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../catalyst/dialog';
import { Input } from '../catalyst/input';
import { Select } from '../catalyst/select';
import { Field, Label } from '../catalyst/fieldset';
import { Switch } from '../catalyst/switch';
import StatCard from '../components/StatCard';
import { CodeStatusBadge } from '../components/StatusBadge';
import { useSupabaseQuery } from '../hooks/useSupabaseQuery';
import { createCode } from '../lib/api';
import type { CodeCreatePayload } from '../lib/api';
import type { Code } from '../types/database';
import { Tag, Plus, Search, Activity, TicketPercent, DollarSign } from 'lucide-react';
import ResourceLink from '../components/ResourceLink';

const PAGE_SIZE = 20;

function formatBenefit(type: string, currency: string | null, value: number | null): string {
	if (type === 'none' || !value) return 'None';
	if (type === 'percent') return `${value}%${currency ? ` ${currency}` : ''}`;
	if (type === 'fixed_amount' && currency === 'product') return `${value} free product${value > 1 ? 's' : ''}`;
	if (type === 'fixed_amount') {
		try {
			return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(value / 100) + (currency ? ` ${currency}` : '');
		} catch { return `${value} ${currency || ''}`; }
	}
	return `${value} ${currency || ''}`;
}

type CodeWithOwner = Code & { users: { display_name: string | null; phone_number: string } | null };

const INITIAL_FORM: CodeCreatePayload & { new_users_only: boolean; status: string } = {
	code: '',
	status: 'active',
	owner_id: '',
	starts_at: '',
	ends_at: '',
	order_max_total_uses: null,
	order_max_uses_per_user: null,
	order_max_unique_users: null,
	owner_max_total_payout_uses: null,
	owner_max_payout_uses_per_user: null,
	owner_max_unique_users: null,
	new_users_only: false,
	redeemer_type: 'none',
	redeemer_currency: null,
	redeemer_value: null,
	owner_type: 'none',
	owner_currency: null,
	owner_value: null,
};

const CodesPage: React.FC = () => {
	const [search, setSearch] = useState('');
	const [filterStatus, setFilterStatus] = useState('');
	const [offset, setOffset] = useState(0);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [form, setForm] = useState(INITIAL_FORM);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);

	// Main codes query with owner join
	const { data: codes, loading, error, refetch, count } = useSupabaseQuery<CodeWithOwner>(
		(client) => {
			let q = client
				.from('codes')
				.select('*, users!codes_owner_id_fkey(display_name, phone_number)', { count: 'exact' })
				.order('created_at', { ascending: false })
				.range(offset, offset + PAGE_SIZE - 1);
			if (filterStatus) q = q.eq('status', filterStatus);
			if (search) q = q.ilike('code', `%${search}%`);
			return q;
		},
		[filterStatus, search, offset]
	);

	// Active codes count
	const { count: activeCount } = useSupabaseQuery<{ id: string }>(
		(client) =>
			client
				.from('codes')
				.select('id', { count: 'exact', head: true })
				.eq('status', 'active'),
		[]
	);

	// Total redemptions count
	const { count: redemptionCount } = useSupabaseQuery<{ id: string }>(
		(client) =>
			client
				.from('code_redemptions')
				.select('id', { count: 'exact', head: true }),
		[]
	);

	// Total discount given
	const { data: discountRows } = useSupabaseQuery<{ discount_cash_cents: number }>(
		(client) =>
			client
				.from('orders')
				.select('discount_cash_cents')
				.gt('discount_cash_cents', 0),
		[]
	);

	const totalDiscount = useMemo(
		() => discountRows.reduce((sum, r) => sum + (r.discount_cash_cents || 0), 0),
		[discountRows]
	);

	const totalPages = count != null ? Math.ceil(count / PAGE_SIZE) : 0;
	const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

	const handleFormChange = (field: string, value: any) => {
		setForm((prev) => ({ ...prev, [field]: value }));
	};

	const handleCreate = async () => {
		setSubmitting(true);
		setSubmitError(null);
		try {
			const payload: CodeCreatePayload = {
				code: form.code,
				status: form.status,
				owner_id: form.owner_id || null,
				starts_at: form.starts_at || null,
				ends_at: form.ends_at || null,
				order_max_total_uses: form.order_max_total_uses,
				order_max_uses_per_user: form.order_max_uses_per_user,
				order_max_unique_users: form.order_max_unique_users,
				owner_max_total_payout_uses: form.owner_max_total_payout_uses,
				owner_max_payout_uses_per_user: form.owner_max_payout_uses_per_user,
				owner_max_unique_users: form.owner_max_unique_users,
				new_users_only: form.new_users_only,
				redeemer_type: form.redeemer_type,
				redeemer_currency: form.redeemer_type !== 'none' ? form.redeemer_currency : null,
				redeemer_value: form.redeemer_type !== 'none' ? form.redeemer_value : null,
				owner_type: form.owner_type,
				owner_currency: form.owner_type !== 'none' ? form.owner_currency : null,
				owner_value: form.owner_type !== 'none' ? form.owner_value : null,
			};
			await createCode(payload);
			setDialogOpen(false);
			setForm(INITIAL_FORM);
			refetch();
		} catch (err: any) {
			setSubmitError(err.message || 'Failed to create code');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="p-6">
			<div className="flex items-center justify-between">
				<Heading level={1}>Codes</Heading>
				<Button onClick={() => { setForm(INITIAL_FORM); setSubmitError(null); setDialogOpen(true); }}>
					<Plus className="size-4" />
					Create Code
				</Button>
			</div>

			{/* Filter bar */}
			<div className="mt-4 flex flex-wrap items-end gap-4">
				<Field className="w-48">
					<Label>Status</Label>
					<Select
						value={filterStatus}
						onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setFilterStatus(e.target.value); setOffset(0); }}
					>
						<option value="">All Statuses</option>
						<option value="active">Active</option>
						<option value="paused">Paused</option>
						<option value="expired">Expired</option>
					</Select>
				</Field>
				<div className="relative w-64">
					<Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400" />
					<Input
						type="text"
						placeholder="Search codes..."
						className="pl-9"
						value={search}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setSearch(e.target.value); setOffset(0); }}
					/>
				</div>
			</div>

			{/* Stats row */}
			<div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
				<StatCard title="Total Codes" value={count ?? '—'} icon={<Tag className="size-5" />} />
				<StatCard title="Active Codes" value={activeCount ?? '—'} icon={<Activity className="size-5" />} />
				<StatCard title="Total Redemptions" value={redemptionCount ?? '—'} icon={<TicketPercent className="size-5" />} />
				<StatCard
					title="Total Discount Given"
					value={discountRows.length > 0
						? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(totalDiscount / 100)
						: '—'}
					icon={<DollarSign className="size-5" />}
				/>
			</div>

			{error && (
				<div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
					{error}
				</div>
			)}

			{loading ? (
				<Text className="mt-6">Loading codes...</Text>
			) : codes.length === 0 ? (
				<Text className="mt-6">No codes found.</Text>
			) : (
				<>
					<Table className="mt-6">
						<TableHead>
							<TableRow>
								<TableHeader>Code</TableHeader>
								<TableHeader>Status</TableHeader>
								<TableHeader>Owner</TableHeader>
								<TableHeader>Redeemer Benefit</TableHeader>
								<TableHeader>Owner Benefit</TableHeader>
								<TableHeader>Order Max Uses</TableHeader>
								<TableHeader>Owner Max Payout Uses</TableHeader>
								<TableHeader>Created</TableHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{codes.map((code) => (
								<TableRow key={code.id} href={`/codes/${code.id}`}>
									<TableCell>
										<span className="font-mono font-bold text-zinc-900 dark:text-white">{code.code}</span>
										<div className="mt-0.5">
											<ResourceLink id={code.id} />
										</div>
									</TableCell>
									<TableCell>
										<CodeStatusBadge status={code.status} />
									</TableCell>
									<TableCell>
										{code.users && code.owner_id ? (
											<ResourceLink
												id={code.owner_id}
												href={`/users/${code.owner_id}`}
												name={code.users.display_name || code.users.phone_number}
											/>
										) : (
											'—'
										)}
									</TableCell>
									<TableCell>
										{formatBenefit(code.redeemer_type, code.redeemer_currency, code.redeemer_value)}
									</TableCell>
									<TableCell>
										{formatBenefit(code.owner_type, code.owner_currency, code.owner_value)}
									</TableCell>
									<TableCell>{code.order_max_total_uses ?? '∞'}</TableCell>
									<TableCell>{code.owner_max_total_payout_uses ?? '∞'}</TableCell>
									<TableCell>{new Date(code.created_at).toLocaleDateString()}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>

					{totalPages > 1 && (
						<div className="mt-4 flex items-center justify-between">
							<Text>
								Page {currentPage} of {totalPages}
								{count != null && ` (${count} codes)`}
							</Text>
							<div className="flex gap-2">
								<Button
									plain
									disabled={offset === 0}
									onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
								>
									Previous
								</Button>
								<Button
									plain
									disabled={currentPage >= totalPages}
									onClick={() => setOffset(offset + PAGE_SIZE)}
								>
									Next
								</Button>
							</div>
						</div>
					)}
				</>
			)}

			{/* Create Code Dialog */}
			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} size="2xl">
				<DialogTitle>Create Code</DialogTitle>
				<DialogBody>
					{submitError && (
						<div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
							{submitError}
						</div>
					)}
					<div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
						<Field>
							<Label>Code *</Label>
							<Input
								type="text"
								required
								value={form.code}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('code', e.target.value)}
								placeholder="e.g. WELCOME10"
							/>
						</Field>
						<Field>
							<Label>Owner ID</Label>
							<Input
								type="text"
								value={form.owner_id || ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('owner_id', e.target.value)}
								placeholder="User ID (optional)"
							/>
						</Field>
						<Field>
							<Label>Status</Label>
							<Select
								value={form.status || 'active'}
								onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFormChange('status', e.target.value)}
							>
								<option value="active">Active</option>
								<option value="paused">Paused</option>
								<option value="expired">Expired</option>
							</Select>
						</Field>
						<Field>
							<Label>Starts At</Label>
							<Input
								type="datetime-local"
								value={form.starts_at || ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('starts_at', e.target.value)}
							/>
						</Field>
						<Field>
							<Label>Ends At</Label>
							<Input
								type="datetime-local"
								value={form.ends_at || ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('ends_at', e.target.value)}
							/>
						</Field>
						<Field>
							<Label>Order Max Total Uses</Label>
							<Input
								type="number"
								value={form.order_max_total_uses ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('order_max_total_uses', e.target.value ? Number(e.target.value) : null)}
							/>
						</Field>
						<Field>
							<Label>Order Max Uses Per User</Label>
							<Input
								type="number"
								value={form.order_max_uses_per_user ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('order_max_uses_per_user', e.target.value ? Number(e.target.value) : null)}
							/>
						</Field>
						<Field>
							<Label>Order Max Unique Users</Label>
							<Input
								type="number"
								value={form.order_max_unique_users ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('order_max_unique_users', e.target.value ? Number(e.target.value) : null)}
							/>
						</Field>
						<Field>
							<Label>Owner Max Total Payout Uses</Label>
							<Input
								type="number"
								value={form.owner_max_total_payout_uses ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('owner_max_total_payout_uses', e.target.value ? Number(e.target.value) : null)}
							/>
						</Field>
						<Field>
							<Label>Owner Max Payouts Per User</Label>
							<Input
								type="number"
								value={form.owner_max_payout_uses_per_user ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('owner_max_payout_uses_per_user', e.target.value ? Number(e.target.value) : null)}
							/>
						</Field>
						<Field>
							<Label>Owner Max Unique Users</Label>
							<Input
								type="number"
								value={form.owner_max_unique_users ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('owner_max_unique_users', e.target.value ? Number(e.target.value) : null)}
							/>
						</Field>
						<Field className="flex items-center gap-3 pt-6">
							<Switch
								checked={form.new_users_only}
								onChange={(checked: boolean) => handleFormChange('new_users_only', checked)}
								color="blue"
							/>
							<Label>New Users Only</Label>
						</Field>

						{/* Redeemer Benefit */}
						<div className="sm:col-span-2">
							<Text className="mb-3 font-medium text-zinc-900 dark:text-white">Redeemer Benefit</Text>
							<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
								<Field>
									<Label>Type</Label>
									<Select
										value={form.redeemer_type || 'none'}
										onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFormChange('redeemer_type', e.target.value)}
									>
										<option value="none">None</option>
										<option value="percent">Percent</option>
										<option value="fixed_amount">Fixed Amount</option>
									</Select>
								</Field>
								<Field>
									<Label>Currency</Label>
									<Select
										value={form.redeemer_currency || ''}
										onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFormChange('redeemer_currency', e.target.value || null)}
										disabled={form.redeemer_type === 'none'}
									>
										<option value="">—</option>
										<option value="credit">Credit</option>
										<option value="cash">Cash</option>
										<option value="product">Product</option>
									</Select>
								</Field>
								<Field>
									<Label>Value</Label>
									<Input
										type="number"
										value={form.redeemer_value ?? ''}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('redeemer_value', e.target.value ? Number(e.target.value) : null)}
										disabled={form.redeemer_type === 'none'}
									/>
								</Field>
							</div>
						</div>

						{/* Owner Benefit */}
						<div className="sm:col-span-2">
							<Text className="mb-3 font-medium text-zinc-900 dark:text-white">Owner Benefit</Text>
							<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
								<Field>
									<Label>Type</Label>
									<Select
										value={form.owner_type || 'none'}
										onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFormChange('owner_type', e.target.value)}
									>
										<option value="none">None</option>
										<option value="percent">Percent</option>
										<option value="fixed_amount">Fixed Amount</option>
									</Select>
								</Field>
								<Field>
									<Label>Currency</Label>
									<Select
										value={form.owner_currency || ''}
										onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFormChange('owner_currency', e.target.value || null)}
										disabled={form.owner_type === 'none'}
									>
										<option value="">—</option>
										<option value="credit">Credit</option>
										<option value="cash">Cash</option>
										<option value="product">Product</option>
									</Select>
								</Field>
								<Field>
									<Label>Value</Label>
									<Input
										type="number"
										value={form.owner_value ?? ''}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('owner_value', e.target.value ? Number(e.target.value) : null)}
										disabled={form.owner_type === 'none'}
									/>
								</Field>
							</div>
						</div>

					</div>
				</DialogBody>
				<DialogActions>
					<Button plain onClick={() => setDialogOpen(false)}>Cancel</Button>
					<Button onClick={handleCreate} disabled={submitting || !form.code}>
						{submitting ? 'Creating...' : 'Create Code'}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default CodesPage;
